import type { Feature, Polygon, Position } from "geojson";
import type { Deal } from "@domain/CRM/deal";
import {
	findJobGeometryProblems,
	type JobGeometry,
	type JobGeometryMark,
	type JobGeometryProblem,
} from "./validateJobGeometry";

/** `[west, south, east, north]`. */
export type GeometryBounds = [number, number, number, number];

/** A place to ring, so that a problem too small to see at the zoom a whole field shows at can still be found. */
export interface GeometryProblemRing {
	/** `[lon, lat]`. */
	point: Position;
	/** How far across, in metres, the painted polygon this marks is; 0 when it marks a position, with no polygon
	 *  painted for it. A polygon big enough on screen to see needs no ring, and this is how to tell. */
	span: number;
}

/** What to draw on the map to point a geometry problem out. */
export interface GeometryProblemHighlight {
	message: string;
	/** The polygons at fault, to paint over. */
	polygons: Feature<Polygon>[];
	rings: GeometryProblemRing[];
	/** Around everything above, for zooming to it; null when the problem has nothing on the map to show. */
	bounds: GeometryBounds | null;
}

/** The validator's code for a defect at a single position (a self-intersection, a nested hole). Painting the
 *  whole polygon would bury the one spot that matters, so these are ringed and nothing more. */
const POSITION_ONLY_CODE = "invalid_polygon";

/** The polygons a mark names: one part of its feature, or every part when the whole feature is at fault. */
function polygonsOf(mark: JobGeometryMark, geometry: JobGeometry): Position[][][] {
	if (mark.layer === "points") return [];
	const target = geometry[mark.layer]?.features[mark.feature]?.geometry;
	if (target?.type === "Polygon") return [target.coordinates];
	if (target?.type !== "MultiPolygon") return [];
	if (mark.part === undefined) return target.coordinates;
	const part = target.coordinates[mark.part];
	return part ? [part] : [];
}

function boundsOf(positions: Position[]): GeometryBounds | null {
	if (positions.length === 0) return null;
	const lons = positions.map((p) => p[0]);
	const lats = positions.map((p) => p[1]);
	return [Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats)];
}

const outerRingsOf = (polygons: Position[][][]): Position[] => polygons.flatMap((polygon) => polygon[0] ?? []);

const METERS_PER_DEGREE = 111_320;

/** The longer side, in metres, of the box around these polygons. Flat-earth arithmetic: this sizes something on
 *  a screen, and a field is far too small for the curve to matter. */
function spanOf(polygons: Position[][][]): number {
	const bounds = boundsOf(outerRingsOf(polygons));
	if (!bounds) return 0;
	const [west, south, east, north] = bounds;
	const metersPerDegreeOfLongitude = METERS_PER_DEGREE * Math.cos((((south + north) / 2) * Math.PI) / 180);
	return Math.max((east - west) * metersPerDegreeOfLongitude, (north - south) * METERS_PER_DEGREE);
}

/** What to draw for one problem, read off the geometry it was found in. */
export function highlightGeometryProblem(problem: JobGeometryProblem, geometry: JobGeometry): GeometryProblemHighlight {
	const marked = problem.marks.map((mark) => ({
		point: mark.point,
		polygons: problem.code === POSITION_ONLY_CODE ? [] : polygonsOf(mark, geometry),
	}));
	const painted = marked.flatMap((mark) => mark.polygons);
	const rings = marked.map((mark) => ({ point: mark.point, span: spanOf(mark.polygons) }));
	return {
		message: problem.message,
		polygons: painted.map((coordinates) => ({
			type: "Feature",
			properties: {},
			geometry: { type: "Polygon", coordinates },
		})),
		rings,
		bounds: boundsOf([...rings.map((ring) => ring.point), ...outerRingsOf(painted)]),
	};
}

/** Whether two polygons have the same rings, vertex for vertex. */
function samePolygon(a: Position[][], b: Position[][]): boolean {
	if (a === b) return true;
	if (a.length !== b.length) return false;
	return a.every((ring, r) => {
		const other = b[r];
		return ring.length === other.length && ring.every((p, i) => p[0] === other[i][0] && p[1] === other[i][1]);
	});
}

/**
 * The polygons at fault that are still among `current` exactly as they were checked.
 *
 * A problem is found in the geometry as it was last saved. Once that geometry is being edited, a polygon that has
 * been reshaped or deleted is no longer the one that was checked, and paint where it used to be points at nothing.
 * One nobody has touched is still the one to go and fix. This says nothing of whether an edit elsewhere has cured
 * it (a boundary moved out around a stray zone): that waits for the geometry to be checked again.
 */
export function unchangedProblemPolygons(
	polygons: Feature<Polygon>[],
	current: readonly Position[][][],
): Feature<Polygon>[] {
	return polygons.filter((polygon) => current.some((other) => samePolygon(polygon.geometry.coordinates, other)));
}

/** Every problem the geo validator would file for this geometry, each with what to draw for it. */
export function highlightJobGeometryProblems(
	geometry: JobGeometry,
	deal: Pick<Deal, "sitesType" | "density" | "isPtsRequired" | "syncMethod"> | null,
	jobFlags: readonly string[],
): GeometryProblemHighlight[] {
	return findJobGeometryProblems(geometry, deal, jobFlags).map((problem) =>
		highlightGeometryProblem(problem, geometry),
	);
}
